import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { workspacesApi, type Workspace, type WorkspaceUpdate } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { Plus, X, Loader2 } from 'lucide-react';

/** Stored JSON list → string[]; anything else (legacy/bad data) → []. */
function parseList(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

type BrandForm = {
  name: string;
  brand_voice: string;
  tone: string;
  target_audience: string;
  website: string;
  ai_caption_guidance: string;
  ai_hashtag_strategy: string;
  brand_colors: string[];
  services: string[];
};

function formFrom(ws: Workspace): BrandForm {
  return {
    name: ws.name ?? '',
    brand_voice: ws.brand_voice ?? '',
    tone: ws.tone ?? '',
    target_audience: ws.target_audience ?? '',
    website: ws.website ?? '',
    ai_caption_guidance: ws.ai_caption_guidance ?? '',
    ai_hashtag_strategy: ws.ai_hashtag_strategy ?? '',
    brand_colors: parseList(ws.brand_colors),
    services: parseList(ws.services),
  };
}

const sameValue = (a: string | string[], b: string | string[]) =>
  JSON.stringify(a) === JSON.stringify(b);

export function SettingsBrandKit() {
  const { getToken } = useAuth();
  const { currentWorkspace, setCurrentWorkspace } = useAppStore();
  const qc = useQueryClient();

  // Load from the server, not the persisted store copy, so edits made by
  // teammates (or in another tab) show up here. Same key as SettingsGeneral.
  const { data: wsData, isError } = useQuery({
    queryKey: ['workspace', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return workspacesApi.get(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });
  const ws = wsData?.workspace?.id === currentWorkspace?.id ? wsData?.workspace : undefined;

  const [name, setName] = useState('');
  const [brandVoice, setBrandVoice] = useState('');
  const [tone, setTone] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [website, setWebsite] = useState('');
  const [captionGuidance, setCaptionGuidance] = useState('');
  const [hashtagStrategy, setHashtagStrategy] = useState('');
  const [brandColors, setBrandColors] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const [newColor, setNewColor] = useState('#6366f1');
  const [newService, setNewService] = useState('');

  const form: BrandForm = {
    name,
    brand_voice: brandVoice,
    tone,
    target_audience: targetAudience,
    website,
    ai_caption_guidance: captionGuidance,
    ai_hashtag_strategy: hashtagStrategy,
    brand_colors: brandColors,
    services,
  };

  // The server values the form was last loaded from. Saves send only the
  // fields that differ from it, so untouched fields never overwrite newer
  // server data.
  const baseline = useRef<{ id: string; values: BrandForm } | null>(null);
  const isDirty = (b: BrandForm) =>
    (Object.keys(b) as (keyof BrandForm)[]).some((k) => !sameValue(form[k], b[k]));

  const applyForm = (f: BrandForm) => {
    setName(f.name);
    setBrandVoice(f.brand_voice);
    setTone(f.tone);
    setTargetAudience(f.target_audience);
    setWebsite(f.website);
    setCaptionGuidance(f.ai_caption_guidance);
    setHashtagStrategy(f.ai_hashtag_strategy);
    setBrandColors(f.brand_colors);
    setServices(f.services);
  };

  useEffect(() => {
    if (!ws) return;
    const fresh = formFrom(ws);
    const prev = baseline.current;
    // Keep the user's in-progress edits if a background refetch lands mid-edit.
    if (prev && prev.id === ws.id && isDirty(prev.values)) return;
    baseline.current = { id: ws.id, values: fresh };
    applyForm(fresh);
    // Replace the stale persisted copy other consumers read from.
    if (JSON.stringify(ws) !== JSON.stringify(currentWorkspace)) setCurrentWorkspace(ws);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws]);

  const update = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const base = baseline.current?.values;
      const changes: WorkspaceUpdate = {};
      for (const k of Object.keys(form) as (keyof BrandForm)[]) {
        if (!base || !sameValue(form[k], base[k])) {
          (changes as Record<string, unknown>)[k] = form[k];
        }
      }
      if (Object.keys(changes).length === 0) return null;
      return workspacesApi.update(token!, ws!.id, changes);
    },
    onSuccess: (data) => {
      if (!data) return;
      // Sync the form to what the server saved too: otherwise fields the server
      // changed (a teammate's edit, an ignored blank name) stay stale in the
      // form and the next save would send them back as "changes".
      const fresh = formFrom(data.workspace);
      baseline.current = { id: data.workspace.id, values: fresh };
      applyForm(fresh);
      setCurrentWorkspace(data.workspace);
      qc.setQueryData(['workspace', data.workspace.id], data);
      qc.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });

  if (!currentWorkspace) {
    return <p className="text-sm text-muted-foreground">No workspace selected</p>;
  }
  if (!ws) {
    return isError ? (
      <p className="text-sm text-destructive">Couldn't load this workspace's Brand Kit.</p>
    ) : (
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
    );
  }

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h2 className="text-base font-semibold text-foreground mb-1">Brand Kit</h2>
        <p className="text-xs text-muted-foreground">
          This context is injected into every AI prompt for this workspace
        </p>
      </div>

      <div className="space-y-4">
        <Field label="Workspace Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-accent"
          />
        </Field>

        <Field label="Brand Voice" hint="e.g. authoritative, friendly, bold">
          <input
            value={brandVoice}
            onChange={(e) => setBrandVoice(e.target.value)}
            placeholder="authoritative"
            className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
          />
        </Field>

        <Field label="Tone" hint="e.g. professional, casual, playful">
          <input
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            placeholder="professional"
            className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
          />
        </Field>

        <Field label="Target Audience">
          <textarea
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            placeholder="Describe your target audience..."
            rows={2}
            className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none focus:ring-1 focus:ring-accent"
          />
        </Field>

        <Field label="Website">
          <input
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://example.com"
            type="url"
            className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
          />
        </Field>

        {/* Brand colors */}
        <Field label="Brand Colors">
          <div className="flex flex-wrap gap-2 mb-2">
            {brandColors.map((color) => (
              <div key={color} className="flex items-center gap-1 bg-tertiary ring-1 ring-border rounded-md px-2 py-1">
                <div
                  className="h-4 w-4 rounded-full border border-border"
                  style={{ backgroundColor: color }}
                />
                <span className="text-xs text-foreground font-mono">{color}</span>
                <button
                  onClick={() => setBrandColors((prev) => prev.filter((c) => c !== color))}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="h-8 w-12 rounded cursor-pointer bg-transparent"
            />
            <input
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              placeholder="#6366f1"
              className="flex-1 bg-input border border-border rounded-md px-3 py-1.5 text-xs font-mono text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
            />
            <button
              onClick={() => {
                if (newColor && !brandColors.includes(newColor)) {
                  setBrandColors((prev) => [...prev, newColor]);
                }
              }}
              className="p-2 bg-tertiary ring-1 ring-border rounded-md text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </Field>

        {/* AI Configuration — workspace voice for caption generation */}
        <div className="pt-2 mt-2 border-t border-hairline">
          <p className="text-xs font-semibold text-foreground mb-1">AI Caption Configuration</p>
          <p className="text-xs text-muted-foreground mb-3">
            Used when you click <span className="text-accent">Generate Caption</span> on a post or bulk upload.
          </p>
        </div>

        <Field
          label="Caption Guidance"
          hint="length, structure, CTA style, emoji policy"
        >
          <textarea
            value={captionGuidance}
            onChange={(e) => setCaptionGuidance(e.target.value)}
            placeholder="Keep captions under 200 chars. Lead with a hook. End with a clear CTA. No emoji."
            rows={3}
            className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none focus:ring-1 focus:ring-accent"
          />
        </Field>

        <Field label="Hashtag Strategy" hint="how many, which kind, where they go">
          <input
            value={hashtagStrategy}
            onChange={(e) => setHashtagStrategy(e.target.value)}
            placeholder="3-5 niche hashtags at the end. No generic tags."
            className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
          />
        </Field>

        {/* Services */}
        <Field label="Services / Products">
          <div className="flex flex-wrap gap-2 mb-2">
            {services.map((svc) => (
              <div key={svc} className="flex items-center gap-1 bg-tertiary ring-1 ring-border rounded-full px-3 py-1">
                <span className="text-xs text-foreground">{svc}</span>
                <button
                  onClick={() => setServices((prev) => prev.filter((s) => s !== svc))}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={newService}
              onChange={(e) => setNewService(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newService.trim()) {
                  setServices((prev) => [...prev, newService.trim()]);
                  setNewService('');
                }
              }}
              placeholder="Add a service or product..."
              className="flex-1 bg-input border border-border rounded-md px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
            />
            <button
              onClick={() => {
                if (newService.trim()) {
                  setServices((prev) => [...prev, newService.trim()]);
                  setNewService('');
                }
              }}
              className="p-2 bg-tertiary ring-1 ring-border rounded-md text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </Field>

        <button
          onClick={() => update.mutate()}
          disabled={update.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md text-sm hover:bg-accent/90 disabled:opacity-50"
        >
          {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save Brand Kit
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-foreground block mb-1.5">
        {label}
        {hint && <span className="text-muted-foreground font-normal ml-1">({hint})</span>}
      </label>
      {children}
    </div>
  );
}
